'use client';

import { Link, Stack, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, ReactNode, useState } from 'react';
import { useThrottledResend } from '../ThrottledResend/useThrottledResend';
import { ResendReserve } from './ResendReserve';

export type LinkSentResendProps = Readonly<{
  /** Slot 16, e.g. "Didn't get it?" - holds across every state, ahead of `resendActionLabel`. */
  leadLine: ReactNode;
  /** Slot 18, e.g. "Resend the email" - the pressable words. Stays in place and stays pressable
   *  (never disabled - see the component doc comment) until the customer's own press sends. */
  resendActionLabel: ReactNode;
  /** Slot 31, e.g. "Please try again later, we will let you know." - the answer to a press made
   *  inside the 60 second window. The caller supplies the whole node directly; there is no number
   *  for this component to receive or interpolate. */
  pressedInsideWindowMessage: ReactNode;
  /** Slot 28, e.g. "A new link replaces the old one. The old link cannot be used." - shown once
   *  the window has elapsed for a customer who never pressed inside it. One of this reserve's
   *  three occupants (see ResendReserve), not a distinguished one - it is simply whichever
   *  occupant is live once the window has elapsed. */
  replacementWarning: ReactNode;
  /** Slot 34, e.g. "You can resend now. A new link replaces the old one. The old link cannot be
   *  used." - shown once the window has elapsed for a customer who DID press inside it (see the
   *  component doc comment, rule 4a): the answer slot 31's "please wait" was promising. Keeps
   *  slot 31's own promise, then repeats slot 28 verbatim so the screen never carries two
   *  different wordings of the one fact. Shown exactly once per press-then-wait cycle - a second,
   *  genuinely past-window press clears it, same as every other occupant here. */
  resendNowMessage: ReactNode;
  /** Called only when a press genuinely sends: past the 60 second window, never inside it (see
   *  the component doc comment for rule 3, tested directly in this component's own test file). */
  onResend: () => void;
  /** Forwarded to useThrottledResend; see its own doc for where the 60-second default comes
   *  from. */
  throttleSeconds?: number;
  /** Disables the control while a resend request is in flight, independently of the countdown. */
  isResending?: boolean;
  'data-testid'?: string;
}>;

/**
 * Fills LinkSent's `resend` slot on the three routes that CAN simply repeat the request that sent
 * the first link: self sign-up by code, an invitation email landing on a tracking reference, and
 * the wallet widget landing on the same. The returning-customer sign-in route cannot reuse this -
 * see LinkSentSignInResend, its own composition, for why (every sign-in link is its own
 * possession check), and for the fifth and final state neither composition shares.
 *
 * Four states, decided by one client timer this component owns via useThrottledResend (reused,
 * never duplicated - see that hook's own doc comment): before any press, nothing shows and the
 * reserve's space is merely held; a press inside the 60 second window answers with slot 31 and
 * sends nothing, because the client blocks the request and a resend genuinely would authorise
 * nothing yet; past the window with no earlier press, slot 28's warning shows automatically (a
 * press now really would replace the link already in the inbox); past the window WITH an earlier
 * press inside it (rule 4a, added for the "repeat routes" task), slot 34 shows instead of slot 28
 * - the customer already knows a press replaces the old link (slot 28 told them nothing new), but
 * they do not yet know the press they already made can be repeated for real, which is what slot
 * 34 tells them. A press in either of the last two states really does send, collapsing back to
 * the first of these four states with a freshly restarted countdown.
 *
 * The design board (settled 2026-09-17) requires this control to be "always live, never
 * disabled" while still making a press inside the window observable, which is a different shape
 * from what useThrottledResend's own `disabled` flag was built for (a real button that goes
 * disabled and enables itself). The fix used here: `resendActionLabel` is never wired to the
 * hook's `disabled` at all - it stays a plain, always-clickable control - and the hook is used
 * purely for its `isCountingDown` state plus `handleResend`, called on every press regardless of
 * `isCountingDown`. Inside the window, `handleResend`'s own internal `disabled` guard already
 * makes it a no-op (see that hook's own doc comment), so no request goes out; this component only
 * additionally records that the press landed inside the window, to answer with slot 31 (a plain
 * string literal, callers pass it directly - so there is nothing left to
 * snapshot; the flag exists purely to gate whether slot 31 OR slot 34 renders, depending on
 * whether the window has ended - see the four-state table above). The flag is cleared only by a
 * REAL past-window send (`handlePress`'s own past-window branch, below) - not by the window
 * merely elapsing - which is what makes rule 4a hold: slot 31 stops answering the moment the
 * window ends (it would be a lie once the window is over), but the fact that this customer
 * pressed early is not forgotten until a real send actually happens, which is exactly the
 * information slot 34 exists to surface.
 *
 * Wrapped in withClarityTheme for the same reason ExpiryClock and LinkSent are: the resend
 * action's colour (`theme.palette.permanent.primary`) only exists on the Clarity theme, and this
 * component has no control over what theme a future caller renders it under. Nesting
 * withClarityTheme inside LinkSent's own (already Clarity) theme is idempotent, so this costs
 * nothing composed the way this file is meant to be used.
 */
function LinkSentResendComponent({
  leadLine,
  resendActionLabel,
  pressedInsideWindowMessage,
  replacementWarning,
  resendNowMessage,
  onResend,
  throttleSeconds,
  isResending,
  'data-testid': dataTestId,
}: LinkSentResendProps): ReactElement {
  const [wasPressedInsideWindow, setWasPressedInsideWindow] = useState(false);

  const { isCountingDown, handleResend } = useThrottledResend({
    onResend,
    throttleSeconds,
    isResending,
  });

  function handlePress() {
    if (isCountingDown) {
      setWasPressedInsideWindow(true);
      return;
    }
    // Past the window: a real send. Clears any stale flag so the fresh countdown this starts
    // renders as the resting "nothing shown" state, not a leftover answer from last time.
    setWasPressedInsideWindow(false);
    handleResend();
  }

  return (
    <Stack
      data-testid={dataTestId}
      alignItems="center"
      sx={(theme: Theme) => ({ gap: theme.semantic.spacing.sm, width: '100%' })}
    >
      <Typography variant="body2" color="text.tertiary">
        {leadLine}{' '}
        <Link
          component="button"
          type="button"
          underline="none"
          data-action="resend"
          onClick={handlePress}
          sx={(theme: Theme) => ({
            font: 'inherit',
            color: theme.palette.permanent.primary,
            fontWeight: theme.semantic.fontWeight.semibold,
            cursor: 'pointer',
          })}
        >
          {resendActionLabel}
        </Link>
      </Typography>
      <ResendReserve
        data-testid="resend-reserve"
        occupants={[
          {
            key: 'replacement-warning',
            content: (
              <Typography variant="body2" color="text.tertiary">
                {replacementWarning}
              </Typography>
            ),
          },
          {
            key: 'pressed-inside-window',
            content: (
              <Typography variant="body2" color="text.tertiary">
                {pressedInsideWindowMessage}
              </Typography>
            ),
          },
          {
            key: 'resend-now',
            content: (
              <Typography variant="body2" color="text.tertiary">
                {resendNowMessage}
              </Typography>
            ),
          },
        ]}
        liveKey={
          isCountingDown
            ? wasPressedInsideWindow
              ? 'pressed-inside-window'
              : null
            : wasPressedInsideWindow
              ? 'resend-now'
              : 'replacement-warning'
        }
      />
    </Stack>
  );
}

export const LinkSentResend = withClarityTheme(LinkSentResendComponent);
